/**
 * User-facing release log, shown in the sidebar "What's new" panel.
 *
 * Write entries for reviewers and coordinators, not for developers: describe
 * what someone can now do differently, not which module changed. Newest
 * version first. Keep to the platform's writing conventions (no em dashes).
 *
 * `audience` controls who sees a bullet:
 *   'reviewer'    only reviewers and above need to know
 *   'coordinator' only coordinators and admins (setup, configuration, cost)
 *   'all'         everyone, including observers
 */

export type ChangelogAudience = "all" | "reviewer" | "coordinator";

export interface ChangelogEntry {
  audience: ChangelogAudience;
  text: string;
}

export interface ChangelogRelease {
  version: string;
  date: string; // ISO date
  entries: ChangelogEntry[];
}

export const CHANGELOG: ChangelogRelease[] = [
  {
    version: "0.14.0",
    date: "2026-10-30",
    entries: [
      {
        audience: "reviewer",
        text: "Tables in a paper are now shown as real tables. In the paper text, a \"Show Table 1 as a table\" button appears where each table starts, because the plain text scrambles a table's rows and columns. The new Tables tab, next to Images, lists every table in the paper with its rows, columns, footnotes and page number.",
      },
      {
        audience: "reviewer",
        text: "\"Copy\" on a table copies it ready to paste into a spreadsheet. Tables are rebuilt from the layout of the PDF without AI, so check values against the PDF, especially in tables with merged cells. A table that could not be rebuilt is still listed with the page it is on.",
      },
    ],
  },
  {
    version: "0.13.1",
    date: "2026-10-30",
    entries: [
      {
        audience: "coordinator",
        text: "Admin, Stats now includes sub-team leaders alongside reviewers and coordinators. They were left out of the list before.",
      },
    ],
  },
  {
    version: "0.13.0",
    date: "2026-10-30",
    entries: [
      {
        audience: "reviewer",
        text: "The extraction page has a new Images tab next to the paper text. It shows the pictures, photos and diagrams pulled out of the paper's PDF. Click one to see it full size, with arrows to move between images.",
      },
      {
        audience: "reviewer",
        text: "Images are only sent to the AI when you ask. Open an image and choose \"Analyse with AI\", or tick several and analyse them together. The AI lists which extraction fields the image shows, with what it saw, and you apply each value you agree with using \"Use this value\". The form's evidence badge for that field then opens the image it came from.",
      },
      {
        audience: "coordinator",
        text: "AI pre-fill no longer sends figure pages to the AI automatically, so you only pay for images a reviewer picked. It can be switched back on in Admin, Settings, AI costs.",
      },
      {
        audience: "coordinator",
        text: "New in Admin, Settings, AI costs: enter Anthropic's price per million tokens and an optional budget, and see the running total of AI spend, broken down by stage (screening, full text, extraction, images, quality appraisal, setup), by review and by person. \"Reset running total\" starts the count again, for example when new credit is loaded, and keeps the all-time total.",
      },
      {
        audience: "coordinator",
        text: "\"Show costs next to AI actions\" (off by default) adds an estimated cost before you click AI pre-fill, image analysis or AI quality ratings, and the actual cost after each AI answer.",
      },
      {
        audience: "reviewer",
        text: "Scanned PDFs, which contain pictures of pages but no text, are now labelled as such. Before, screening said the AI had used the full text when it had only had the abstract. The scanned pages appear under Images, where you can ask the AI to read the ones you need.",
      },
    ],
  },
  {
    version: "0.12.0",
    date: "2026-10-29",
    entries: [
      {
        audience: "reviewer",
        text: "There is a new Completed tab after Extraction. Once both a paper's data extraction and its quality appraisal are confirmed, it moves there, so the Extraction list only shows work still to do. Completed papers can still be opened and changed, and marking either one as not confirmed moves the paper back. The Extraction list now also shows the quality appraisal state of each paper.",
      },
      {
        audience: "all",
        text: "The dashboard's counts for each review now only reflect what has happened in that review. Previously a paper screened in one review could also be counted as progressing in another review it belonged to.",
      },
      {
        audience: "reviewer",
        text: "Each review now uses one quality appraisal tool for every paper, so the tool choice has gone from the quality appraisal page. The page shows which tool your review uses and what it is for. Papers appraised earlier with a different tool still open as they were saved.",
      },
      {
        audience: "coordinator",
        text: "The quality appraisal tool for a review is now set on the review's card in Admin, Reviews, and the sub-team leader of that review can set it too.",
      },
      {
        audience: "reviewer",
        text: "On the extraction and quality appraisal pages you can now see what has happened: \"Unsaved changes\" appears while you have edits, \"Save draft\" turns into \"Saved\", and confirming turns the button green (\"Extraction confirmed\" or \"Assessment confirmed\"). Click it again to mark the work as not confirmed. Leaving the page with unsaved changes now asks first.",
      },
      {
        audience: "reviewer",
        text: "Fixed: saving an extraction draft failed whenever any field was still empty, without telling you. Drafts now save however complete they are.",
      },
      {
        audience: "reviewer",
        text: "At the full-text stage, every criterion must now be marked (met, not met or unclear) and a short reason given before you can include, exclude or mark a paper Uncertain. If anything is missing, the page tells you exactly what. At title and abstract, both stay optional. The criteria table also appears when there is no AI suggestion, so you can always mark them.",
      },
      {
        audience: "coordinator",
        text: "Every AI call now records how many tokens it used. The prompt panel next to \"AI suggestion\" shows them, and AI pre-fill and AI quality ratings show them after they run, so the cost per paper can be estimated. Suggestions made before this release show no token count.",
      },
      {
        audience: "reviewer",
        text: "The AI criteria table now shows each criterion exactly as it is written for your review, rather than a shortened version, with the AI summary of its decision directly below.",
      },
      {
        audience: "reviewer",
        text: "The Title/Abstract, Full Text and Extraction lists now show every paper waiting, 20 to a page, with a running total (for example \"Showing 1 to 20 of 156 pending\") and Previous and Next buttons. In card view the arrows carry on to the next page.",
      },
      {
        audience: "coordinator",
        text: "The Conflicts tab now only lists papers where reviewers really disagree, and each card says why it is there: reviewers disagree, or a reviewer could not decide from the full paper. Papers whose decisions agreed but had not moved on (for example after lowering the number of reviewers needed) no longer appear, and changing the number of reviewers needed now moves such papers on straight away.",
      },
      {
        audience: "reviewer",
        text: "Papers that another reviewer has already moved on no longer stay in your Title/Abstract list. If you try to decide on one from an old tab, you are told it has moved on and nothing is recorded.",
      },
    ],
  },
  {
    version: "0.11.2",
    date: "2026-10-22",
    entries: [
      {
        audience: "all",
        text: "If you have not been added to a review yet, the dashboard, Screening and Administration pages now tell you so and who to contact, instead of showing an empty page or a loading spinner that never finishes.",
      },
      {
        audience: "reviewer",
        text: "Your dashboard now only counts the reviews you work on.",
      },
      {
        audience: "coordinator",
        text: "Unallocated papers (not yet in any review) are now only visible to coordinators and admins, and only they can mark one as excluded. Previously reviewers could see and change them too.",
      },
    ],
  },
  {
    version: "0.11.1",
    date: "2026-10-19",
    entries: [
      {
        audience: "reviewer",
        text: "Your column in the AI criteria table now has three buttons: a tick for met, a cross for not met, and a question mark for unclear. They describe the criterion itself, not whether you agree with the AI. Once you choose, your answer shows with the same wording and colours as the AI column so you can compare them at a glance, and a small arrow lets you change it before you submit.",
      },
      {
        audience: "all",
        text: "The sidebar can now be collapsed to icons only, giving more room to the paper you are reviewing. Use the button at the top of the sidebar; your choice is remembered in this browser.",
      },
    ],
  },
  {
    version: "0.11.0",
    date: "2026-10-08",
    entries: [
      {
        audience: "reviewer",
        text: "Quality appraisal now includes COSMIN, for studies that test how well an assessment measures what it should (validity, reliability, responsiveness). Rate the boxes the study covers, mark the rest Not applicable, and the page shows the lowest rating so you can use it as the overall rating, as COSMIN requires. QUADAS-2, RoB 2, PEDro and the custom checklist are all still available.",
      },
      {
        audience: "reviewer",
        text: "\"Suggest ratings with AI\" now uses the tool you have selected and reads the uploaded PDF. Previously it could rate with a different tool and work from the abstract only.",
      },
      {
        audience: "coordinator",
        text: "Each review can now have its own default quality appraisal tool (Admin, Reviews, Edit criteria), for example COSMIN for a review of assessment tools. Reviewers can still choose another tool for an individual study.",
      },
      {
        audience: "reviewer",
        text: "The AI suggestion is now much quicker to read: up to three key points about the paper, a table showing whether the AI thinks each inclusion criterion is met, and a short summary. Exclusion criteria only appear when the AI thinks one applies or cannot tell; the rest are one click away.",
      },
      {
        audience: "reviewer",
        text: "Each criterion in the table has a \"You\" column where you can mark it met or not met. Rows where you disagree with the AI are highlighted, your marks are saved with your decision, and when you exclude a paper the reason is filled in from them for you.",
      },
      {
        audience: "all",
        text: "Click or hover the small prompt icon next to \"AI suggestion\" to see exactly what was sent to the AI for that paper, and copy it. Suggestions made before this release do not have their prompt recorded.",
      },
      {
        audience: "coordinator",
        text: "\"Re-run AI\" now always asks the AI again. Previously it could quietly show the earlier answer, for example after you edited a review's criteria.",
      },
    ],
  },
  {
    version: "0.10.2",
    date: "2026-10-08",
    entries: [
      {
        audience: "all",
        text: "You now stay signed in while you use the platform. Previously you were quietly signed out after 15 minutes, which made pages look empty until you reloaded. You only need to sign in again after 30 days without using it.",
      },
      {
        audience: "reviewer",
        text: "If the AI suggestion for a paper cannot be produced, the screening card now tells you why, just below the decision buttons, instead of showing nothing. You can always screen the paper yourself.",
      },
    ],
  },
  {
    version: "0.10.1",
    date: "2026-10-08",
    entries: [
      {
        audience: "reviewer",
        text: "Marking a paper as Uncertain at the title and abstract stage now sends it on to full-text review, where the full paper can settle it. Previously an Uncertain paper could quietly disappear from every queue.",
      },
      {
        audience: "reviewer",
        text: "If AI pre-fill for data extraction fails, you now see why and nothing on the form is changed. Previously it could come back blank with no explanation.",
      },
      {
        audience: "coordinator",
        text: "The Conflicts tab now also lists full-text papers that reviewers marked Uncertain, and conflicts you have already resolved no longer stay on the list.",
      },
      {
        audience: "coordinator",
        text: "Observer accounts can no longer screen papers or edit extractions. Upgrade someone to Reviewer and assign them to a review to give them access.",
      },
    ],
  },
  {
    version: "0.10.0",
    date: "2026-08-21",
    entries: [
      {
        audience: "reviewer",
        text: "When you include or exclude a paper, you can now tick which of the review's own inclusion or exclusion criteria applied, alongside your usual free-text note. This is optional but makes your decision faster to record and easier to review later.",
      },
      {
        audience: "coordinator",
        text: "Reviews now have a structured inclusion criteria checklist to go with the existing exclusion reasons list (Admin, Reviews). Both can be generated from your criteria with AI or edited by hand, and both show up as checkboxes during screening.",
      },
      {
        audience: "reviewer",
        text: "Fixed a bug where reviewers assigned to more than one review could not switch between them from the Screening page: the review switcher was silently failing to load your reviews. It now loads correctly.",
      },
    ],
  },
  {
    version: "0.9.2",
    date: "2026-08-21",
    entries: [
      {
        audience: "coordinator",
        text: "Admins now have a \"Reset all review data\" option (Admin, Settings, Danger zone) for clearing out studies, screening decisions, and extractions from testing before onboarding real reviewers. It keeps your review criteria, search strings, extraction subtopics, and everyone's account exactly as they are. It requires typing a confirmation phrase and cannot be undone.",
      },
    ],
  },
  {
    version: "0.9.1",
    date: "2026-08-21",
    entries: [
      {
        audience: "all",
        text: "You can now list your areas of expertise on your profile (ask a coordinator to add them for you, in Admin, Users, Expertise). This helps coordinators decide who to allocate a paper to.",
      },
      {
        audience: "coordinator",
        text: "When allocating a paper to a reviewer, you can now click \"Suggest reviewer\" to get an AI suggestion based on each assigned reviewer's stated expertise. It only ever suggests: you still choose and confirm who the paper goes to.",
      },
    ],
  },
  {
    version: "0.9.0",
    date: "2026-08-21",
    entries: [
      {
        audience: "all",
        text: "Reviewers now need to be explicitly assigned to a review before they can screen it, instead of automatically seeing every review on the platform. If you are a reviewer and a review you expect to see is missing, ask a coordinator or your sub-team leader to add you.",
      },
      {
        audience: "coordinator",
        text: "Each review now has a Reviewers panel (Admin, Reviews, Reviewers) to search for a reviewer or sub-team leader and grant or revoke their access to that review.",
      },
      {
        audience: "coordinator",
        text: "You can optionally allocate specific papers within a review to a specific reviewer (Admin, Reviews, Allocate studies). This is entirely optional: a review that never uses it works exactly as before, with every assigned reviewer seeing the full queue.",
      },
      {
        audience: "coordinator",
        text: "Sub-team leaders can now manage reviewer assignment and paper allocation for their own review, from a cut-down Admin view showing just that review.",
      },
    ],
  },
  {
    version: "0.8.0",
    date: "2026-08-21",
    entries: [
      {
        audience: "coordinator",
        text: "Coordinators can now create and edit reviews and change platform settings directly, without needing an Admin. Admin accounts are now reserved for deleting things and managing Coordinator or Admin accounts.",
      },
      {
        audience: "coordinator",
        text: "A new account type, Sub-team Leader, is available for reviewers who help run a review. More capabilities for this role, including managing who works on a review and assigning specific papers to specific reviewers, are coming soon.",
      },
    ],
  },
  {
    version: "0.7.1",
    date: "2026-08-20",
    entries: [
      {
        audience: "coordinator",
        text: "Fixed a search string bug: when you generated all 9 database variants together, the IEEE Xplore version could list its concepts in a different order to the other 8 (for example, technology terms first instead of population terms first). Boolean AND means this never changed which records the search actually matched, but it made the variants hard to compare side by side. All 9 now use the same concept order.",
      },
      {
        audience: "coordinator",
        text: "Fixed \"Fetch results\" failing against PubMed with a URI Too Long error. A search string with many synonyms is long enough that PubMed's servers rejected it when sent the usual way; it is now sent in a way that has no such limit.",
      },
      {
        audience: "all",
        text: "AI screening for title and abstract now runs the moment you open a paper that does not have a suggestion yet, instead of needing a whole batch to be screened in advance. It happens quietly in the background and appears just below the paper within a few seconds. This means AI cost only builds up for papers someone actually looks at, not for everything a search brought back.",
      },
      {
        audience: "coordinator",
        text: "Fetch results and bulk import now show a live progress bar while records and AI suggestions are being processed, and the review's numbers update automatically once finished. No more need to refresh the page to see whether it has caught up.",
      },
    ],
  },
  {
    version: "0.7.0",
    date: "2026-08-19",
    entries: [
      {
        audience: "coordinator",
        text: "The recommended search string panel has moved below the inclusion and exclusion criteria, since a good search string depends on both being filled in first. The Generate, Regenerate with AI button is now always visible there, no need to open framework editing first.",
      },
      {
        audience: "coordinator",
        text: "Clicking Regenerate now shows a proper confirmation inside the app instead of a browser popup.",
      },
      {
        audience: "coordinator",
        text: "Fixed the IEEE Xplore search string: it was only searching abstracts, which are often too short to mention interaction or design details. It now searches the whole paper, using the field syntax IEEE actually requires.",
      },
      {
        audience: "coordinator",
        text: "Fetching results from a database now tells you exactly what happened (records found, imported, and skipped as duplicates) right in the page, instead of only through an email that did not always arrive.",
      },
      {
        audience: "coordinator",
        text: "Fixed a bug where regenerating search strings could fail silently and leave a broken string in place. A failed generation now changes nothing and tells you to try again.",
      },
      {
        audience: "all",
        text: "Fixed an import bug affecting papers with a single author or special characters in the title, which could stop an entire PubMed search from completing. Author names with accents, such as Noël, are also now stored correctly instead of as garbled text.",
      },
    ],
  },
  {
    version: "0.6.0",
    date: "2026-08-18",
    entries: [
      {
        audience: "reviewer",
        text: "AI extraction can now read figures. When a paper has diagrams, screenshots or photos of the apparatus, those pages are shown to the AI alongside the text, so details that only appear in an image (what the participant sees, how targets are laid out, which headset was used) can be picked up. Any value taken from a figure tells you which page it came from, and still needs your confirmation like every other field.",
      },
      {
        audience: "coordinator",
        text: "Search strings can now be run directly against PubMed, Europe PMC and OpenAlex. Use the \"Fetch results\" button next to the search string in Admin, Reviews: results are imported with their abstracts and added to the review automatically, and the run is logged for your PRISMA audit trail.",
      },
      {
        audience: "coordinator",
        text: "OpenAlex covers HCI and XR venues such as ACM CHI, ACM ETRA and IEEE VR, which clinical databases do not index. This matters for the tools and designs review, where relevant systems are often published outside medical journals.",
      },
      {
        audience: "coordinator",
        text: "Imported records that arrive without an abstract now have one looked up automatically from their DOI. Abstracts cannot always be found, so the import summary tells you how many records still have none. Those screen poorly, since the AI has only the title to work from.",
      },
      {
        audience: "coordinator",
        text: "Figure reading can be turned off, or limited to fewer pages per paper, in Admin, Settings if you want to reduce AI cost.",
      },
    ],
  },
  {
    version: "0.5.0",
    date: "2026-08-13",
    entries: [
      {
        audience: "all",
        text: "Accounts created for you by an administrator now ask you to choose your own password the first time you sign in. Nobody else knows your password after that.",
      },
    ],
  },
  {
    version: "0.4.0",
    date: "2026-08-07",
    entries: [
      {
        audience: "coordinator",
        text: "Studies that look like duplicates of an existing record are now held in Admin, Duplicates for you to confirm or reject, instead of being hidden automatically. Only exact DOI and PubMed ID matches are treated as certain.",
      },
    ],
  },
  {
    version: "0.3.0",
    date: "2026-07-06",
    entries: [
      {
        audience: "reviewer",
        text: "Data extraction now works side by side with the paper. AI suggestions carry the exact sentence they came from, highlighted in the text, and you can highlight your own passages, link them to a field, and add notes.",
      },
      {
        audience: "reviewer",
        text: "You can add your own extraction subtopics per review, and the guidance you write for them is passed to the AI.",
      },
    ],
  },
];

/** Shown on the sidebar badge. Always the newest release. */
export const CURRENT_VERSION = CHANGELOG[0]?.version ?? "0.0.0";
